import { useCallback, useState } from 'react';
import type { KeyboardEvent } from 'react';

import MuiDialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

import { ActionableAlert } from './ActionableAlert';
import { ErrorsAlert } from './ErrorsAlert';
import type { ActionableMessage } from './ActionableMessages';

export interface ComplianceIssues {
  // Refused by the back when the action they hinder is performed.
  readonly blocking: readonly ActionableMessage[];
  readonly warnings: readonly ActionableMessage[];
}

interface Props {
  readonly blockingTitle: string;
  readonly id: string;
  readonly issues: ComplianceIssues;
  readonly title: string;
}

export const ComplianceDialog = ({
  blockingTitle,
  id,
  issues: { blocking, warnings },
  title,
}: Readonly<Props>) => {
  const [isOpen, setIsOpen] = useState(true);
  const closeDialog = useCallback(() => {
    setIsOpen(false);
  }, []);
  // Some pages take the focus back after the opening: the dialog then holds it on its frame, not
  // on its button, and Enter would do nothing. Its buttons and links keep their own Enter.
  const closeOnEnter = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    const isOnControl =
      event.target instanceof HTMLElement &&
      !!event.target.closest('a, button');

    if (event.key === 'Enter' && !isOnControl) {
      event.preventDefault();
      setIsOpen(false);
    }
  }, []);

  if ((!blocking.length && !warnings.length) || !isOpen) {
    return null;
  }

  return (
    <MuiDialog
      aria-describedby={`${id}-description`}
      aria-labelledby={`${id}-title`}
      onClose={closeDialog}
      onKeyDown={closeOnEnter}
      open={isOpen}
    >
      <DialogTitle id={`${id}-title`}>{title}</DialogTitle>
      <DialogContent id={`${id}-description`}>
        <Stack spacing={2}>
          <ErrorsAlert errors={blocking} title={blockingTitle} />
          <ActionableAlert
            items={warnings}
            severity="warning"
            title="À noter"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          autoFocus
          onClick={closeDialog}
          size="large"
          variant="contained"
        >
          J&apos;ai compris
        </Button>
      </DialogActions>
    </MuiDialog>
  );
};
